import { Injectable, OnDestroy } from '@angular/core';
import { UserClaims } from '@okta/okta-auth-js';
import { BehaviorSubject, map, Observable, ReplaySubject, Subject, takeUntil } from 'rxjs';
import { firstValueOrDefault } from 'src/util/rxjs/observables/first-value';
import { AuthProxyService } from './auth-proxy.service';

type FlightLogClaims = UserClaims & { EmployeeNumber?: string };

@Injectable({
  providedIn: 'root',
})
export class AuthStateService implements OnDestroy {
  private _claims$ = new BehaviorSubject<UserClaims | undefined>(undefined);
  private _accessToken$ = new ReplaySubject<string | undefined>(1);
  private _impersonatedId$ = new ReplaySubject<string | undefined>(1);
  private _impersonatedId?: string;
  private _onDestroy$ = new Subject<void>();

  public async isAuthenticated(): Promise<boolean> {
    return await firstValueOrDefault(this.accessToken$.pipe(map((token) => token != null)), false);
  }

  get claims(): {
    value: FlightLogClaims | undefined;
    valueChanges: Observable<FlightLogClaims | undefined>;
  } {
    return {
      value: this._claims$.value,
      valueChanges: this._claims$.asObservable(),
    };
  }

  get accessToken$(): Observable<string | undefined> {
    return this._accessToken$.asObservable();
  }

  get impersonatedUserId(): {
    value: string | undefined;
    valueChanges: Observable<string | undefined>;
  } {
    return {
      value: this._impersonatedId,
      valueChanges: this._impersonatedId$.asObservable(),
    };
  }

  constructor(private _authProxy: AuthProxyService) {
    this._authProxy.claims$.pipe(takeUntil(this._onDestroy$)).subscribe((claims) => {
      this._claims$.next(claims);
    });
    this._authProxy.accessToken$.pipe(takeUntil(this._onDestroy$)).subscribe((token) => {
      this._accessToken$.next(token);
    });
    this._authProxy.impersonatedUserId$.pipe(takeUntil(this._onDestroy$)).subscribe((id) => {
      this._impersonatedId = id;
      this._impersonatedId$.next(id);
    });
  }

  ngOnDestroy(): void {
    this._onDestroy$.next();
    this._onDestroy$.complete();
  }
}
